import { FC, useMemo } from 'react';
import { Box, Grid2, Stack, Typography, useTheme } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import { Certification } from 'src/types/Certification';
import { fadeStyle } from 'src/components/NewRenterInterface/constants';
import CertificationCard from 'src/components/NewRenterInterface/Household/CertificationCard';
import CertificationHistoryIcon from 'src/icons/CertificationHistoryIcon';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';

const RenterHistoryPage: FC = () => {
  const theme = useTheme();
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[] | undefined;

  const inactiveCertifications = useMemo(() => {
    if (!certifications?.length) {
      return [];
    }
    return certifications
      .filter((cert) => cert.status?.renter_active === false)
      .slice()
      .sort((a, b) => {
        const aDate = a.completed_date || a.updated || a.expiration_date || '';
        const bDate = b.completed_date || b.updated || b.expiration_date || '';
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      });
  }, [certifications]);

  if (!inactiveCertifications.length) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 280,
          textAlign: 'center',
          ...fadeStyle,
        }}
      >
        <HistoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No application or re-certification history yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Inactive or completed applications and re-certifications will appear
          here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
      <PageHeader
        title="Application & Re-certification History"
        icon={
          <CertificationHistoryIcon
            size={24}
            color={theme.palette.common.white}
          />
        }
        subtitle="Past and inactive applications and re-certifications for your households."
      />
      <Stack gap={2} sx={{ p: 3, ...fadeStyle }}>
        <Grid2 container spacing={2}>
          {inactiveCertifications.map((cert) => (
            <Grid2
              size={{ xs: 12, sm: 6, md: 6 }}
              key={cert.id}
              sx={{ display: 'flex' }}
            >
              <CertificationCard cert={cert} />
            </Grid2>
          ))}
        </Grid2>
      </Stack>
    </Box>
  );
};

export default RenterHistoryPage;
